/**
 * A query-builder component defines a step like query, consisting of
 * selection of events and exclusion steps
 */

import {produce} from 'immer';
import findKey from 'lodash/findKey';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import {ParamComponent} from 'panel';

import {getReportUrl, getWorkspaceId} from 'common/report/util';
import {dataGroupIDsEqual, serializeAttributes} from 'common/util';
import {mpColors} from 'common/util/colors';
import {emitTrackEvent, propagateEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {DisplayContext} from 'common/widgets/date-range-picker/models/types';
import {ReportName, ResourceType, SPECIAL_ALL_EVENT_VALUES} from 'common/report/constants';
import {
  EntrySectionUpdateEvent,
  FiltersOperator,
  isEventBasedEntry,
  isFormulaEntry,
  isGroupedEntry,
  isMetricEntry,
  isUserBasedEntry,
  MetricEntry,
  QueryEntry,
  QueryFilterEntry,
  QueryMetricEntry,
  QuerySegmentEntry,
  RowLabelType,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {customEventsClient} from 'common/report/queries/app-api/custom-events-client';
import {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {maxQueryReason} from 'common/report/util/query-tracking';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {DataGroup, SelectWarning} from 'common/widgets/data-group-selector/types';
import {dataGroupsClient} from 'common/report/queries/app-api/data-groups-client';
import {DEFAULT_DATA_GROUP} from 'common/widgets/data-group-selector';
import {waitForStoreValue} from 'common/report/stores/store';
import {topEventsStore} from 'common/report/stores/events';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';
import {
  fetchGlobalEventPropertyDefinitions,
  fetchPropertyDefinitionsForEvents,
  peoplePropertyDefinitionsStore,
} from 'common/report/stores/property-definitions';
import {currentProjectStore} from 'common/report/stores/project-details';
import {stickyFixed} from 'common/util/dom-sticky';
import {chromeHeaderPrimaryHeight} from 'common/widgets/chrome/chrome-header/constants';
import {customPropertiesStore} from 'common/report/stores/custom-properties';
import {TrackingUsageContext} from 'common/report/tracking-setup';
import {propMenuPrefetchEnabled} from 'common/report/util/rollouts';
import {EventsController} from 'common/query-builder/query-block/contexts/events-controller';
import {QueryContextRegistry} from 'common/query-builder/query-block/contexts/registry';
import {spaNavManager} from 'common/report/util/spa-nav-manager';
import {DisplaySize} from 'common/components/tooltip/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {DisplayContext as FilterDisplayContext} from 'common/widgets/property-filter-menu/models/types';
import {PropertyFilterFactory} from 'common/widgets/property-filter-menu/models/utils';
import {ProfileHistoryType} from 'common/types/reports/query';
import {MetricsResource} from 'common/report/model-resources/metrics';

import {getSegmentType, isUserSegment} from '../query-block/segment-menu/types';
import {IncludeCustomPropertyType} from '../query-block/properties-menu/types';
import {isBehaviorSegmentation} from '../query-entry-section/row-types/behavior/utils';
import {BEHAVIOR_PROPERTIES} from '../query-block/properties-menu/constants';
import {formulaToFormulaClause} from '../insights-formula/types';
import {CAPTURED_TRACKING, TrackingEvent} from './tracking';
import {Filter, QueryBuilderParams, QueryBuilderSelection, ReportSwitchEvent} from './types';
import {queryIsInvalidForDataGroup, setQueryDataGroup} from './util/data-groups';
import {
  flattenReferencedEntries,
  getMathTypeFromEntry,
  isMultiAttributionSegment,
  isXauMathType,
  MismatchedFilterAndSegment,
  qbStyleAttrs,
  sanitizeExclusionSteps,
  sanitizeFilters,
  sanitizeGroupBys,
  sanitizeMultiAttributionFilter,
  transformFromQuerySegmentEntries,
  transformToQuerySegmentEntries,
} from './util';
import {template} from './template';

type EventSourceName = `entries` | `exclusion-steps`;

const COMPONENT_NAME = `mp-query-builder`;

const EVENT_SOURCE_MAP = Object.freeze({
  entries: `entries`,
  'exclusion-steps': `exclusionSteps`,
});

interface QueryBuilderState {
  customEvents: CustomEventDefinition[];
  dataGroups: DataGroup[];
  selectionObject: QueryBuilderSelection;
}

export class QueryBuilder extends ParamComponent<QueryBuilderParams, QueryBuilderState, QueryContextRegistry> {
  get config() {
    return {
      params: {
        // Label to use on the main events section
        addEntryLabel: String,
        addEntryButtonLabel: String,
        addEntryButtonTooltipText: String,
        addExclusionEventLabel: String,
        allowCohortFilter: Boolean,
        allDataGroups: Boolean,
        behaviorMode: Boolean,
        breakdownAttribution: Array,
        breakdownAttributionSelection: String,
        // list of ResourceTypes to use in the breakdown menu
        breakdownResourceTypes: Array,
        canCustomizeNumericBuckets: Boolean,
        canUseMultiAttribution: Boolean,
        // Allow adding additional events at the same entry index
        canGroupEvents: Boolean,
        // Remove natural border & margins to fit inside another container that has its own border & margins
        contained: Boolean,
        chartType: String,
        switchableReportName: String,
        dataGroupId: String,
        dataGroupsEnabled: Boolean,
        dateRange: Object,
        dateUnit: String,
        disableAddEventFilters: Boolean,
        disableAddEntry: Boolean,
        disableAddEntryReason: String,
        disableAddGroupedEventReason: String,
        disableBreakdown: Boolean,
        disableCloneEntry: Boolean,
        disableDeleteEntry: Boolean,
        disableFirstTimeEventFilters: Boolean,
        disableProfileHistory: Boolean,
        enableEnhancedListFilters: Boolean,
        enableFilterAttribution: Boolean,
        enableHideEntry: Boolean,
        reportHeaderParams: Object,
        // interface: QueryMetricEntry[] | MetricEntry[]
        entries: Array,
        minEntries: Number,
        // interface: QuerySuperEntry
        emptyPlaceholder: Object,
        // enable drag-and-drop reordering on main entry selection
        entriesDraggable: Boolean,
        // if the row index label can be toggled
        entryIndexToggleable: Boolean,
        entryIndexToggleIcon: String,
        entryRowLabelType: String,
        // whether to hide autotrack events from the events menu
        excludeAutotrackEvents: Boolean,
        // whether to hide custom events from the events menu
        excludeCustomEvents: Boolean,
        // whether to hide dimension properties from properties menus
        excludeDimensionProperties: Boolean,
        // whether to hide custom properties from properties menus
        // enum: [`none`, `persisted-only`, `persisted-non-behavioral-only`, `all`]
        includeCustomProperties: String,
        // interface: QueryMetricEntry[]
        exclusionSteps: Array,
        exclusionStepAddEntryTooltipText: String,
        // Formulas
        formulas: Array,
        formulasDisableReason: String,
        formulasError: Object,
        filterHideUsers: Boolean,
        filterHideDataGroups: Boolean,
        // interface: EventFilters
        filters: Array,
        // enum: [FiltersOperator.And, FiltersOperator.Or]
        filtersOperator: String,
        // interface: Segment
        groupBy: Array,
        // Array of indices for group by rows that cannot be dragged
        groupByUndraggableRows: Array,
        // Section title for the main event selection
        headerTitle: String,
        // Section title in lower case
        headerTitleLowercase: Boolean,
        // overrides the cohort permissions and allow inline cohort creation/editing
        inlineCohortsOverride: Object,
        // renders query builder entry section in read only mode
        readonlyEntries: Boolean,
        // max days time menu can query into data history
        maxDataHistoryDays: Number,
        // enables numeric property selection and aggregation functions
        metricsMode: Boolean,
        groupByLimitation: Number,
        mainEntryNonCollapsible: Boolean,
        otherGroupByOptions: Array,
        otherFilterOptions: Array,
        otherAggregationOptions: Array,
        plotStyle: String,
        projectHasPeople: Boolean,
        projectHasCohorts: Boolean,
        projectIsFreePlan: Boolean,
        projectId: Number,
        // ToggleOption[]
        queryTypeOptions: Array,
        rowLimit: Number,
        // Default row types. Interface: RowTypeOption[]
        rowTypes: Array,
        selectedQueryType: String,
        showEntryRename: Boolean,
        showExclusionSteps: Boolean,
        hideEditControls: Boolean,
        hideDatePicker: Boolean,
        hideDataGroupsSelector: Boolean,
        hideFilter: Boolean,
        hideGroupBy: Boolean,
        // If true, Top Events cannot be queried in combination with any other event
        restrictTopEventsOnly: Boolean,
        showAllEvents: Boolean,
        showAnyEvent: Boolean,
        showSessions: Boolean,
        showSpecialDatasetProperty: Boolean,
        showTopEvents: Boolean,
        slowlyChangingDimensionsPocEnabled: Boolean,
        // whether b2b cohorts are enabled
        supportB2bCohorts: Boolean,
        timeDisplayContext: String,
        // If specified, dispatches '[mp-query-builder] <event>' bubbled events that can be listened for tracking with "Report name" as a property
        trackName: String,
        // If specified, adds extra properties to the track event
        trackProperties: Object,
        reportName: String,
        userId: Number,
        workspaceId: Number,
        // as of date for projects that do not have real time ingestion
        projectAsOfDate: String,
        reportHeaderOnly: Boolean,

        // VNode objects for rendering
        countMenu: Object,
        footerItems: Object,
        bodySection: Object,
      },

      defaultParams: {
        addEntryLabel: ``,
        addExclusionEventLabel: `Add exclusion step`,
        breakdownResourceTypes: [],
        entries: [],
        entryRowLabelType: RowLabelType.Numeric,
        includeCustomProperties: IncludeCustomPropertyType.None,
        exclusionSteps: [],
        filters: [],
        filtersOperator: FiltersOperator.And,
        groupBy: [],
        groupByUndraggableRows: [],
        groupByLimitation: null,
        formulas: [],
        formulasDisableReason: ``,
        formulasError: null,
        otherGroupByOptions: [],
        otherFilterOptions: [],
        otherAggregationOptions: [],
        rowTypes: [ResourceType.Event],
        timeDisplayContext: DisplayContext.FullLength,
        trackProperties: {},
        projectAsOfDate: ``,
      },

      hooks: {
        preUpdate: (_state, newParams?: QueryBuilderParams) => {
          if (!newParams) return;
          const shouldUpdateSelectionObject = [
            [this.params.breakdownAttributionSelection, newParams.breakdownAttributionSelection],
            [this.params.dataGroupId, newParams.dataGroupId],
            [this.params.dateRange, newParams.dateRange],
            [this.params.entries, newParams.entries],
            [this.params.exclusionSteps, newParams.exclusionSteps],
            [this.params.filters, newParams.filters],
            [this.params.filtersOperator, newParams.filtersOperator],
            [this.params.groupBy, newParams.groupBy],
            [this.params.formulas, newParams.formulas],
            [this.params.formulasDisableReason, newParams.formulasDisableReason],
            [this.params.formulasError, newParams.formulasError],
            [this.params.emptyPlaceholder, newParams.emptyPlaceholder],
          ].some(([oldParam, newParam]) => !isEqual(oldParam, newParam));
          const shouldFetchProjectData = [
            [this.params.projectId, newParams.projectId],
            [this.params.workspaceId, newParams.workspaceId],
          ].some(([oldParam, newParam]) => !isEqual(oldParam, newParam));
          if (shouldUpdateSelectionObject) {
            /**
             * a single object representing the current
             * selection state of all passed attributes
             */

            // Whenever we get new data attributes, we buffer them to this.selectionObject. This is
            // because within a frame, we might have multiple update events, from multiple components
            // handling callbacks underneath. Since the frame hasn't finished, the attributes are not
            // updated yet, so reading from them would give us a outdated view of the world. Instead, we
            // use selectionObject to buffer any changes to the state within a frame, and reset it when
            // we come to the next frame (when attributes are changed)
            this.update({selectionObject: this.getSelectionObjectFromParams(newParams)});
            if (this.initialized && propMenuPrefetchEnabled) {
              this._prefetchProperties();
            }
          } else if (shouldFetchProjectData && this.initialized) {
            this.fetchProjectData({
              projectId: newParams.projectId,
              workspaceId: newParams.workspaceId,
            });
          }
        },
      },

      template,

      // p75 from https://mixpanel.com/s/2kFNfR
      slowThreshold: 535,

      contexts: [`eventsController` as const, `metrics` as const],

      defaultContexts: {
        eventsController: new EventsController(),
        metrics: new MetricsResource(),
      },

      defaultState: {
        customEvents: [],
        dataGroups: [DEFAULT_DATA_GROUP],
        selectionObject: null,
      },

      helpers: {
        serializeAttributes,
        handleReportHeaderInsert: ({elm}) => {
          const wireStickyHeader = () => {
            // call stickyFixed, and wire-up clean up to run on disconnect
            // ideally we don't want to pass in the height directly here but i'm sure there was a good reason why we did here
            this.onDisconnected(stickyFixed(elm, chromeHeaderPrimaryHeight));
          };

          if (this.helpers.useStickyHeader()) {
            wireStickyHeader();
          } else {
            waitForStoreValue(currentProjectStore, {timeout: 5_000}).then(
              () => {
                if (this.helpers.useStickyHeader()) {
                  wireStickyHeader();
                }
              },
              () => {
                /* Swallow any timeout */
              },
            );
          }
        },
        useStickyHeader: (): boolean => !this.params.reportHeaderOnly,
        reportShouldHaveStickyHeader: (): boolean =>
          [`insights`, `funnels`, `flows`].includes(this.params.switchableReportName),
        isReportSupportedOnDashboard: (): boolean =>
          [`insights`, `funnels`, `retention`, `flows`].includes(this.params.reportHeaderParams.displayContext),
        getHeaderActionItemsSlots: () => this.getHeaderActionItemsSlots(),
        getPropertyMenuAttributes: (type) => this.getPropertyMenuAttributes(type),
        getFilterEntryRowType: () => SelectorTypeDefinitions.Filter,
        getSegmentEntryRowType: () => SelectorTypeDefinitions.Segment,
        getNoneRowLabelType: () => RowLabelType.None,
        getDataGroupSelectWarnings: () => this.getDataGroupSelectWarnings(),
        getQueryTypeOptions: () => {
          return [
            {
              items: this.params.queryTypeOptions.map((item) => {
                return {
                  label: item.label,
                  tooltip: {
                    size: DisplaySize.Small,
                    text: item.tooltipLabel,
                  },
                  value: item.value,
                };
              }),
            },
          ];
        },
        getSelectedQueryType: () => {
          const queryTypeOption = this.params.queryTypeOptions.find(
            (item) => item.value === this.params.selectedQueryType,
          );
          return queryTypeOption?.label;
        },
        getSwitchableReports: () => {
          return [
            {
              value: ReportName.Insights,
              icon: `insights-tab`,
              iconColor: mpColors[`$insights-icon`],
              label: `Insights`,
              tooltipLabel: `Insights Report`,
            },
            {
              value: ReportName.Funnels,
              icon: `funnels-tab`,
              iconColor: mpColors[`$funnels-icon`],
              label: `Funnels`,
              tooltipLabel: `Funnels Report`,
            },
            {
              value: ReportName.Flows,
              icon: `flows-tab`,
              iconColor: mpColors[`$flows-icon`],
              label: `Flows`,
              tooltipLabel: `Flows Report`,
            },
            {
              value: ReportName.Retention,
              icon: `retention-tab`,
              iconColor: mpColors[`$retention-icon`],
              label: `Retention`,
              tooltipLabel: `Retention Report`,
            },
          ];
        },
        hasEventsOnly: () => this.hasEventsOnly,
        hasUserEntriesOnly: () => this.hasUserEntriesOnly,
        onDataGroupSelected: ({detail: {dataGroup}}: CustomEvent) => this.emitUpdateEvent({dataGroupId: dataGroup.id}),
        onSavedCustomEvent: () => this.emitUpdateEvent({}),
        handleQueryTypeHovered: (ev) => {
          spaNavManager.preloadAppAssets(
            `/project/${currentProjectStore.value?.id}/view/${getWorkspaceId()}/app/${ev.detail.hovered}`,
          );
        },
        onQueryTypeSelected: (ev) => this.emitUpdateEvent({queryType: ev.detail.selected || ev.detail.item.value}),
        onReportTypeSelected: (ev) => {
          const detail = {
            report: ev.detail.selected,
            newUrlPath: getReportUrl(ev.detail.selected, {
              projectId: this.params.projectId,
              workspaceId: this.params.workspaceId,
            }),
          };

          const event: ReportSwitchEvent = new CustomEvent(`reportswitch`, {detail, bubbles: true, composed: true});
          this.dispatchEvent(event);
        },
        preventTracking: (ev) => ev.stopPropagation(),
        propagateEvent: (ev) => propagateEvent(this, ev),
        updateGroupBy: ({detail}) =>
          this.emitUpdateEvent({groupBy: detail.items || detail.item}, {queryReason: detail.queryReason}),

        selectorTheme: () => qbStyleAttrs().selectorTheme,
        selectorType: () => qbStyleAttrs().selectorType,

        updateAttributionSelection: ({detail}: CustomEvent) => {
          const props = {
            selected_breakdown_attribution_value: detail.item.value,
            selected_breakdown_attribution_type: detail.item.index,
          };
          this.track(TrackingEvent.SelectBreakdownAttribution, props);
          this.emitUpdateEvent({attributionSelection: detail.item.value});
        },
        /** Captures a track event to be tracked as part of this component */
        captureTracking: (ev) => {
          const {eventPrefix, props} = ev.detail;
          const eventName = ev.detail.eventName.replace(eventPrefix, ``);

          if (this.isCapturedTrackEvent(eventName)) {
            this.helpers.preventTracking(ev);
            this.track(eventName, props);
          }
        },

        getEntries: (source): Array<QueryEntry | MetricEntry> => this.getEntries(source),

        getEntryValues: (source): string[] => {
          return this.getEntryValues(source);
        },

        getNumExclusionSteps: () => this.getEntries(`entries`).length,

        /** Attribution related helpers */
        showAttributionSelector: () => {
          return Boolean(this.helpers.attributionSelectorLabelPrefix());
        },

        attributionSelectorLabelPrefix: () => {
          const attributionSelectorActions = [];
          const {groupBy, filters} = this.state.selectionObject;
          // This is funnels specific logic to determine attribution selection.
          // Oddly, enable-filter-attribution is only used by funnels.
          if (this.params.enableFilterAttribution) {
            const hasEventBreakdown = groupBy.some(
              (item) =>
                getSegmentType(item) === ResourceType.Event ||
                (isUserSegment(item) &&
                  item[ResourceType.User].profileHistoryOptions?.type === ProfileHistoryType.EventTime),
            );
            if (hasEventBreakdown) {
              attributionSelectorActions.push(`Segment`);
            }

            const hasEventFilter = filters.some(
              (f) =>
                f.resourceType === ResourceType.Event ||
                (f.resourceType === ResourceType.User &&
                  f.profileHistoryOptions?.type === ProfileHistoryType.EventTime),
            );
            if (hasEventFilter) {
              attributionSelectorActions.push(`Filter`);
            }
          } else {
            const validBreakdowns = groupBy.filter(
              (segment) => ![ResourceType.Cohort, ResourceType.User].includes(getSegmentType(segment)),
            );
            if (this.params.breakdownAttribution?.length > 1 && validBreakdowns.length > 0) {
              attributionSelectorActions.push(`Segment`);
            }
          }
          return attributionSelectorActions.sort().join(` + `);
        },

        getAttributionSelectedOption: () => {
          const items = this.params.breakdownAttribution;
          for (const item of items) {
            if (item.value === this.params.breakdownAttributionSelection) {
              return item.label;
            }
          }
          return ``;
        },

        showFilterOrOperatorInfo: () => {
          return this.params.filtersOperator === FiltersOperator.Or;
        },
        /** Attributes for mp-query-entry-section metric analysis */
        getMetricsModeAttributes: () => {
          if (!this.params.metricsMode) {
            return {};
          }

          return serializeAttributes({
            'restrict-top-events-only': this.params.restrictTopEventsOnly,
            'row-label-type': `alpha`,
            'show-all-events': true,
            'show-top-events': this.params.showTopEvents,
          });
        },

        updateEntrySection: ({detail: {entries, queryReason, requery}}: EntrySectionUpdateEvent, source) => {
          this.updateEntries(entries as Array<QueryEntry | MetricEntry>, source, {queryReason, requery});
        },

        getFilterEntries: (): QueryFilterEntry[] => {
          return this.params.filters.map((filterItem) => ({
            event: filterItem,
            // global filters do not render and/or
            filtersOperator: null,
            filters: [],
            type: SelectorTypeDefinitions.Filter,
          }));
        },

        getSegmentEntries: (): QuerySegmentEntry[] => {
          return transformToQuerySegmentEntries(this.params.groupBy);
        },

        updateFilterEntrySection: ({detail: {entries, queryReason}}: EntrySectionUpdateEvent) => {
          const filters: Filter[] = (entries as QueryFilterEntry[]).map((entry) => entry.event);
          this.emitUpdateEvent({filters}, {queryReason});
        },

        updateSegmentEntrySection: ({detail: {entries, queryReason}}: EntrySectionUpdateEvent) => {
          const groupBy = transformFromQuerySegmentEntries(entries as QuerySegmentEntry[]);
          this.emitUpdateEvent({groupBy}, {queryReason});
        },

        actionClick: (ev) => {
          const action = ev.detail.value;

          if (action === `refresh`) {
            this._refreshEventsAndProperties();
          }
          propagateEvent(this, ev);
        },

        updateFormulaSection: ({detail: {formulas, trackingInfo}}) => {
          emitTrackEvent(this, {
            eventName: trackingInfo.event,
            props: formulaToFormulaClause(trackingInfo.formula).trackingData(),
          });
          this.emitUpdateEvent({formulas});
        },
      },
    };
  }

  getAllEntries() {
    return flattenReferencedEntries(this.state.selectionObject.entries, this.getContext(`metrics`));
  }

  get hasEventsOnly() {
    const allEntries = this.getAllEntries();
    // TODO some empty states from transforms don't pass in type, fix to remove the !entry.type check
    return allEntries.every(
      (entry) => !entry || isEventBasedEntry(entry) || isGroupedEntry(entry) || isFormulaEntry(entry),
    );
  }

  get hasUserEntriesOnly() {
    const allEntries = this.getAllEntries();
    return allEntries.every((entry) => isUserBasedEntry(entry));
  }

  get hasUserEntriesAndNoCohorts() {
    const allEntries = this.getAllEntries();
    const hasCohortMetrics = allEntries.some((entry) => entry[`type`] === ResourceType.Cohort);
    return !hasCohortMetrics && allEntries.some((entry) => isUserBasedEntry(entry));
  }

  private dispatchUpdate: (ev: CustomEvent) => void;
  private pendingQueryReason: QueryReason;

  constructor() {
    super();
    // we use debounce such that even if we have multiple updates within a frame, we send the last
    // one up, to not spam network queries.
    this.dispatchUpdate = debounce((ev) => {
      ev.detail.queryReason = this.pendingQueryReason;
      delete this.pendingQueryReason;
      this._dispatchUpdate(ev);
    }, 100);
    this.update({
      selectionObject: this.getSelectionObjectFromParams(this.params),
    });
  }

  public connectedCallback() {
    // TODO: We have two instances of query builder in the report: 1) for the report header, 2) for the sidebar
    // QB. Disable the events controller for report header to save on unnecessary data processing in eventsController.
    // This can be removed once the report header is refactored into a different component from the sidebar QB.
    if (this.params.reportHeaderOnly) {
      this.getContext(`eventsController`).blockUpdates(this);
    }

    super.connectedCallback();

    this.update({
      selectionObject: this.getSelectionObjectFromParams(this.params),
    });

    this.fetchProjectData({
      projectId: this.params.projectId,
      workspaceId: this.params.workspaceId,
    });
  }

  private getHeaderActionItemsSlots() {
    const controlItems = this.params.reportHeaderParams.controlItems;
    return controlItems.filter((item) => Object.prototype.hasOwnProperty.call(item, `slot`));
  }

  private getSelectionObjectFromParams(newParams: QueryBuilderParams): QueryBuilderSelection {
    const {
      breakdownAttributionSelection,
      dataGroupId,
      dateRange,
      entries,
      exclusionSteps,
      filters,
      filtersOperator,
      groupBy,
      formulas,
    } = newParams;
    return {
      attributionSelection: breakdownAttributionSelection,
      dataGroupId,
      dateRange,
      entries,
      exclusionSteps,
      filters,
      filtersOperator,
      groupBy,
      formulas,
    };
  }

  private getDataGroupSelectWarnings(): SelectWarning[] {
    return this.state.dataGroups?.map((dataGroup) => {
      const dataGroupId = dataGroup.groupID;
      const showWarning = queryIsInvalidForDataGroup({
        dataGroupId,
        query: this.state.selectionObject,
        customEvents: this.state.customEvents,
        metricsResource: this.getContext(`metrics`),
      });
      return {dataGroupId, showWarning};
    });
  }

  // TODO(pablo): this method seems to add unnecessary indirection, these values
  // can be computed directly in the template
  private getPropertyMenuAttributes(type: string) {
    const groupsEnabled = this.params.dataGroupsEnabled;
    const dataGroupsEnabledAttr = `data-groups-enabled`;
    const isXAU = this.state.selectionObject.entries.some((entry) => isXauMathType(getMathTypeFromEntry(entry)));
    const disabledBehaviors = (reason) =>
      Array.from(BEHAVIOR_PROPERTIES.keys()).reduce((acc, key) => {
        acc[key] = `${BEHAVIOR_PROPERTIES.get(key).label.replace(`...`, ``)} ${reason}`;
        return acc;
      }, {});
    const XAU_REASON = `is disabled with Rolling XAU analysis, remove Rolling XAU to enable`;
    const COHORT_REASON = `is disabled with cohort analysis, remove cohorts to enable`;

    const attrs = {
      'include-custom-properties': this.params.includeCustomProperties,
      'track-name': this.params.trackName,
      'report-name': this.params.reportName,
    };

    let otherAttrs;
    if (this.hasEventsOnly) {
      const types = this.params.breakdownResourceTypes;
      if (types.length && type === `breakdown`) {
        otherAttrs = {
          [dataGroupsEnabledAttr]: false,
          'project-has-cohorts': types.includes(ResourceType.Cohort),
          'project-has-people': types.includes(ResourceType.User),
          'hide-events': !types.includes(ResourceType.Event),
          'disabled-items': !types.includes(ResourceType.Event) || isXAU ? disabledBehaviors(XAU_REASON) : null,
        };
      } else {
        otherAttrs = {
          [dataGroupsEnabledAttr]: groupsEnabled,
          'project-has-cohorts': this.params.projectHasCohorts,
          'project-has-people': this.params.projectHasPeople,
          'hide-events': false,
          'disabled-items': isXAU ? disabledBehaviors(XAU_REASON) : null,
        };
      }
    } else {
      otherAttrs = {
        [dataGroupsEnabledAttr]: groupsEnabled && (type === `breakdown` ? this.hasUserEntriesOnly : true),
        'project-has-cohorts': this.params.projectHasCohorts,
        // when combining cohorts & events, only user filtering is possible
        'project-has-people':
          (type === `breakdown` ? this.hasUserEntriesAndNoCohorts : true) && this.params.projectHasPeople,
        'hide-events': true,
        'disabled-items': disabledBehaviors(COHORT_REASON),
      };
    }

    return {...attrs, ...otherAttrs};
  }

  private getEntries(source: EventSourceName): Array<QueryEntry | MetricEntry> {
    return this.params[EVENT_SOURCE_MAP[source]].map((entry: QueryMetricEntry | MetricEntry) => {
      return produce(entry, (draft) => {
        if (!draft.type) {
          draft.type = ResourceType.Event;
        }
      });
    });
  }

  private getEntryValues(source: EventSourceName): string[] {
    return this.getEntries(source).flatMap((entry) => {
      if (isMetricEntry(entry)) {
        if (entry.behavior.behaviors?.length) {
          return entry.behavior.behaviors.map((b) => b.name);
        }
        return entry.behavior.name;
      }
      if (isGroupedEntry(entry)) {
        return entry.event.map((event) => event.event.value);
      } else if (isEventBasedEntry(entry)) {
        return entry.event.value;
      }
    });
  }

  private updateEntries(
    entries: Array<QueryEntry | MetricEntry>,
    source: EventSourceName,
    {queryReason, requery = true}: {queryReason?: QueryReason; requery?: boolean} = {},
  ) {
    this.emitUpdateEvent({[EVENT_SOURCE_MAP[source]]: entries}, {queryReason, requery});
  }

  private emitUpdateEvent(
    updates: Partial<QueryBuilderSelection>,
    {requery = true, queryReason = QueryReason.QbOtherUpdate}: {requery?: boolean; queryReason?: QueryReason} = {},
  ) {
    // this is to handle an edge case for fast-edit board -> report
    // the event handler may be called BEFORE component initialization (child component store callback)
    // so here we use `this.initialized` to see which one is the "latest" selection
    // note that we cannot always use `this.params.*` because query-builder does maintains some intermediate state like drag & drop feature.
    // in long run we may need to refactor d&d so intermediate state is no longer maintained within this component so we could get rid of `selectionObject`
    const existingSelection = this.initialized
      ? this.state.selectionObject
      : this.getSelectionObjectFromParams(this.params);
    let selection = {...existingSelection, ...updates};
    const originalSegmentLength = selection.groupBy.length + selection.filters.length;
    const allEntries = flattenReferencedEntries(selection.entries, this.getContext(`metrics`));

    selection = {
      ...selection,
      exclusionSteps: sanitizeExclusionSteps(selection.exclusionSteps, selection.entries),
      groupBy: sanitizeGroupBys({
        entries: selection.entries,
        segments: selection.groupBy,
        metricsResource: this.getContext(`metrics`),
      }),
    };
    const filters = selection.filters.map((filter) =>
      PropertyFilterFactory.createPropertyFilter(filter, {displayContext: FilterDisplayContext.Default}),
    );
    // TODO always sanitize by making tests okay with it
    if (filters.some((filter) => isBehaviorSegmentation(filter))) {
      selection.filters = sanitizeFilters(allEntries, filters);
    }
    let hasRemovedInlineFilter = false;
    selection.entries.forEach((entry) => {
      let originalFilter = isMetricEntry(entry) ? entry.behavior.filters : entry.filters;
      if (!originalFilter) originalFilter = [];
      const originalFilterLength = originalFilter.length;
      const sanitizedInlineFilters = sanitizeFilters([entry], originalFilter);
      const newFilterLength = sanitizedInlineFilters.length;
      if (originalFilterLength !== newFilterLength) {
        hasRemovedInlineFilter = true;
      }
      if (isMetricEntry(entry)) {
        entry.behavior.filters = sanitizedInlineFilters;
      } else {
        entry.filters = sanitizedInlineFilters;
      }
    });

    if (hasRemovedInlineFilter || originalSegmentLength !== selection.groupBy.length + selection.filters.length) {
      console.error(`removing breakdown/filter`, {
        hasRemovedInlineFilter,
        originalSegmentLength,
        groupByLength: selection.groupBy.length,
        filtersLength: selection.filters.length,
      });
      // href to current location will redirect to the current pre-change clean URL
      addToast({
        message: `Incompatible breakdowns and filters were removed`,
        ctaHref: window.location.href,
        cta: `Undo`,
      });
    }

    // Handle sanitizing multi attribution filters based on breakdown.
    // Make sure to do it after the above toast because we want a unique message
    // indicating the specific incompatibility.
    if (selection.groupBy.some((segment) => isMultiAttributionSegment(segment))) {
      // Since all the filters are required to share the same property, we just need the last one.
      let lastMismatchedFilterAndSegment: MismatchedFilterAndSegment;
      ({
        results: selection.filters,
        mismatchedFilterAndSegment: lastMismatchedFilterAndSegment,
      } = sanitizeMultiAttributionFilter(filters, selection.groupBy));

      if (lastMismatchedFilterAndSegment) {
        addToast({
          message: `Removed attribution filter ${lastMismatchedFilterAndSegment.filterLabel} because it does not match breakdown property ${lastMismatchedFilterAndSegment.segmentLabel}.`,
          ctaHref: window.location.href,
          cta: `Undo`,
        });
      }
    }

    if (
      Object.prototype.hasOwnProperty.call(updates, `dataGroupId`) &&
      !dataGroupIDsEqual(updates.dataGroupId, existingSelection.dataGroupId)
    ) {
      selection = setQueryDataGroup({
        query: selection,
        dataGroupId: updates.dataGroupId,
        customEvents: this.state.customEvents,
        metricsResource: this.getContext(`metrics`),
      });
    }
    const detail = {
      ...selection,
      requery,
      delta: updates,
    };
    this.update({selectionObject: selection});

    this.pendingQueryReason = maxQueryReason([this.pendingQueryReason, queryReason]);
    this.dispatchUpdate(new CustomEvent(`update`, {detail}));
  }

  private _dispatchUpdate(ev) {
    this.dispatchEvent(ev);
  }

  /**
   * Emits track events that bubble up
   */
  private track(event: string, props: UnknownObj = {}) {
    if (!this.params.trackName) {
      return;
    }

    if (this.isCapturedTrackEvent(event)) {
      emitTrackEvent(this, {
        eventName: event,
        props: {
          project_id: this.params.projectId,
          tab: this.params.trackName,
          source: `query-builder`,
          ...(this.params.queryTypeOptions ? {'query type': this.params.selectedQueryType} : {}),
          // props from propagated track is last so that they can override any props added by this
          // component
          ...props,
          // intentionally put after so it override
          [`usage context`]: TrackingUsageContext.QueryBuilder,
        },
        cancelable: true,
      });
    } else {
      throw new Error(`tracking event not defined: ${event}`);
    }
  }

  private isCapturedTrackEvent(event: string) {
    return findKey(TrackingEvent, (eventName) => eventName === event) || CAPTURED_TRACKING.has(event);
  }

  private fetchDataGroups({projectId, workspaceId}) {
    return dataGroupsClient.query({projectId, workspaceId});
  }

  private fetchCustomEvents({projectId, workspaceId}) {
    return customEventsClient.query({projectId, workspaceId});
  }

  private fetchProjectData({projectId, workspaceId}: {projectId: number; workspaceId: number}) {
    if (!projectId && !workspaceId) {
      // This check is necessary because retention adds mp-query-builder to the dom before setting attributes, so
      // they are blank when this method is called in connectedCallback
      return;
    }
    const paramsUnchanged = () => projectId === this.params.projectId && workspaceId === this.params.workspaceId;

    this.fetchCustomEvents({projectId, workspaceId}).then((customEvents) => {
      if (paramsUnchanged()) {
        this.update({customEvents});
      }
    });
    if (this.params.dataGroupsEnabled) {
      this.fetchDataGroups({projectId, workspaceId}).then((dataGroups) => {
        if (paramsUnchanged()) {
          this.update({dataGroups: [DEFAULT_DATA_GROUP, ...dataGroups]});
        }
      });
    }
  }

  _refreshEventsAndProperties() {
    const projectId = this.params.projectId;
    const workspaceId = this.params.workspaceId;

    topEventsStore.refresh({projectId, workspaceId});
    eventDefinitionsStore.refresh({projectId, workspaceId});
    peoplePropertyDefinitionsStore.refresh({projectId, workspaceId});
    this._prefetchProperties({refresh: true});
  }

  /**
   * Prefetch property definitions for all selected events
   */
  _prefetchProperties({refresh = false} = {}) {
    // TODO: add data group properties prefetching
    if (refresh) {
      customPropertiesStore.refresh({
        projectId: this.params.projectId,
        workspaceId: this.params.workspaceId,
      });
    } else {
      customPropertiesStore.fetch({
        projectId: this.params.projectId,
        workspaceId: this.params.workspaceId,
      });
    }

    const eventNames = this.state.selectionObject.entries
      .map((prop) => (prop.type === ResourceType.Event ? prop.event.value : null))
      .filter(Boolean);

    if (eventNames.length > 0) {
      const includeDimensionProperties = !this.params.excludeDimensionProperties;

      // Prefetch global definitions for tooltips.
      fetchGlobalEventPropertyDefinitions({refresh});

      // Prefetch definitions for all events. This is used in global query blocks.
      fetchPropertyDefinitionsForEvents(eventNames, {includeDimensionProperties, refresh});

      for (const event of eventNames.filter((value) => SPECIAL_ALL_EVENT_VALUES.includes(value))) {
        // Prefetch definitions for each events that is not representiive of all events.
        // This is used for inline query blocks.
        fetchPropertyDefinitionsForEvents([event], {includeDimensionProperties, refresh});
      }
    }
  }
}

export const QueryBuilderEl = defineAndMakeJsxFactory(COMPONENT_NAME, QueryBuilder);
export const QueryBuilderReact = panel2react(COMPONENT_NAME, QueryBuilder);
